import { useMemo, useState } from "react";
import { Search } from "lucide-react";

// Curated set with searchable keywords. Keywords are lowercased, space-separated.
// Designed to match what caregivers actually type when adding tiles ("happy",
// "sleep", "snack", "mom", "school"…).
const EMOJI_INDEX = [
  // Communication / generic
  ["💬", "talk speech speak chat say"],
  ["🔤", "letters word text generic"],
  ["📁", "folder category"],
  ["✨", "sparkle star magic"],
  ["⭐", "star favorite"],
  ["🌟", "shining star"],
  // Feelings
  ["😊", "happy smile glad"],
  ["😢", "sad cry tears"],
  ["😠", "angry mad frustrated"],
  ["😨", "scared afraid fear"],
  ["😴", "sleepy tired sleep"],
  ["🤩", "excited proud wow"],
  ["😌", "calm relax content"],
  ["🤕", "hurt pain"],
  ["❤️", "love heart"],
  ["🤪", "silly goofy"],
  ["🥰", "love affection"],
  ["🥺", "please beg sorry"],
  ["😎", "cool proud"],
  ["😤", "frustrated mad"],
  ["😟", "worried"],
  ["😳", "embarrassed"],
  ["💛", "love yellow heart feelings"],
  ["💫", "feelings dizzy"],
  // Food
  ["🍎", "apple fruit"],
  ["🍌", "banana fruit"],
  ["🍓", "strawberry fruit berry"],
  ["🍇", "grape fruit"],
  ["🥕", "carrot vegetable"],
  ["🥦", "broccoli vegetable"],
  ["🍗", "chicken meat"],
  ["🍖", "meat lamb"],
  ["🍞", "bread"],
  ["🍪", "cookie snack"],
  ["🍕", "pizza"],
  ["🍦", "ice cream sweet"],
  ["🥛", "milk drink dairy"],
  ["🧃", "juice drink"],
  ["💧", "water drink"],
  ["🍴", "fork eat eating"],
  ["🥤", "drink cup"],
  ["🥗", "salad"],
  ["🍱", "lunch food"],
  ["🍚", "rice"],
  ["🥙", "wrap food hummus"],
  ["🌴", "dates palm"],
  ["🥯", "bagel"],
  ["🥐", "pastry"],
  ["🌾", "grain wheat quinoa"],
  ["🥜", "nuts"],
  ["🫘", "beans"],
  ["🥢", "tofu chopsticks"],
  // People & family
  ["👋", "hi hello wave goodbye greet"],
  ["👨", "man dad father"],
  ["👩", "woman mom mother"],
  ["🧒", "child kid"],
  ["👶", "baby"],
  ["👨‍👩‍👦", "family"],
  ["👩‍🏫", "teacher"],
  ["👵", "grandma grandmother"],
  ["👴", "grandpa grandfather"],
  ["🧑‍⚕️", "doctor nurse"],
  ["🧑‍🍳", "chef cook"],
  ["🧑‍🤝‍🧑", "friend friends"],
  // Activities
  ["🎈", "party balloon fun"],
  ["🎮", "game video play"],
  ["📖", "book reading"],
  ["📚", "library books"],
  ["🛏️", "bed sleep"],
  ["📺", "tv watch"],
  ["🎤", "sing music"],
  ["🎵", "music song"],
  ["🎨", "art draw paint"],
  ["⚽", "soccer ball play"],
  ["🏀", "basketball recess play"],
  ["🧱", "blocks build"],
  ["🚙", "car drive"],
  ["🧸", "teddy toy"],
  ["🪀", "yoyo toy"],
  ["🎲", "dice game"],
  ["🚶", "walk go"],
  ["🛝", "playground slide"],
  ["🎠", "carousel ride"],
  ["🛴", "scooter ride"],
  // Body / care
  ["🤲", "help open hands give"],
  ["🦶", "foot leg"],
  ["💪", "strong arm"],
  ["👀", "look see eyes"],
  ["👃", "smell nose"],
  ["👅", "taste tongue"],
  ["👄", "mouth lip"],
  ["🦷", "tooth dentist"],
  ["🪥", "toothbrush brush teeth"],
  ["🧠", "brain think"],
  ["🩺", "doctor stethoscope"],
  ["💊", "medicine pill"],
  // Places
  ["🏠", "home house"],
  ["🏫", "school"],
  ["🌳", "tree park outside"],
  ["🛒", "shopping store"],
  ["🚽", "bathroom toilet potty"],
  ["🏥", "hospital doctor"],
  ["🏖️", "beach"],
  ["🚌", "bus"],
  ["🏪", "store shop"],
  ["🏘️", "neighborhood community"],
  ["📮", "mail post"],
  ["💇", "haircut salon"],
  // Potty / care
  ["💦", "pee water wet"],
  ["💩", "poop"],
  ["🎉", "celebrate party congrats"],
  ["🧻", "tissue toilet paper wipe"],
  ["🧼", "soap wash"],
  ["☀️", "sun dry"],
  ["🏁", "done finish"],
  // Ya/no/control
  ["✅", "yes ok done"],
  ["❌", "no stop"],
  ["➕", "more add"],
  ["➖", "less remove"],
  ["🛑", "stop"],
  ["🆘", "help emergency"],
  ["🤝", "agree handshake"],
  ["👍", "yes good thumbs up"],
  ["👎", "no bad thumbs down"],
  ["🙏", "please thank pray"],
  ["⬆️", "up please"],
  ["⬇️", "down please"],
  // Misc useful
  ["🥳", "celebrate party birthday"],
  ["🎂", "birthday cake"],
  ["🎄", "christmas tree"],
  ["🌙", "moon eid night"],
  ["🕎", "hanukkah menorah"],
  ["🪔", "diwali lamp"],
  ["🦃", "thanksgiving turkey"],
  ["🎆", "fireworks new year"],
  ["🥚", "egg easter"],
  ["✡️", "kosher star david"],
  ["🥘", "halal stew dish"],
  ["💵", "money cash"],
  ["📱", "phone"],
  ["🔑", "keys"],
  ["💼", "work job"],
  ["🗓️", "calendar appointment"],
];

export default function EmojiPicker({ value, onChange, "data-testid": testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return EMOJI_INDEX;
    return EMOJI_INDEX.filter(([_, kw]) => kw.includes(needle));
  }, [q]);

  return (
    <div className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="brutal-input flex items-center gap-3"
        data-testid="emoji-picker-toggle"
      >
        <span className="text-3xl leading-none">{value || "🔤"}</span>
        <span className="text-sm text-[color:var(--ink-soft)]">Tap to choose</span>
      </button>
      {open && (
        <div
          className="absolute z-30 mt-2 brutal-card p-3 bg-white"
          style={{ width: "min(380px, 92vw)" }}
        >
          <div className="flex items-center gap-2 mb-2 px-1 py-1 rounded-xl border-[3px] border-[color:var(--ink)] bg-[color:var(--paper-muted)]">
            <Search size={14} className="text-[color:var(--ink-soft)] ml-1" />
            <input
              autoFocus
              placeholder="Search: happy, snack, school…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm py-1"
              data-testid="emoji-search"
            />
          </div>
          <div className="grid grid-cols-8 gap-1 max-h-60 overflow-y-auto tile-scroll" data-testid="emoji-results">
            {filtered.map(([e]) => (
              <button
                type="button"
                key={e}
                onClick={() => {
                  onChange(e);
                  setOpen(false);
                  setQ("");
                }}
                className="text-2xl rounded-lg p-1 hover:bg-[color:var(--cream-deep)]"
                data-testid={`emoji-option-${e}`}
              >
                {e}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-8 text-sm text-[color:var(--ink-soft)] py-4 text-center">
                No icons found. Try a simpler word.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
